import React, { useCallback, useRef, useEffect } from 'react';
import { useTransformable } from '../contexts/TransformableContext';

interface TransformableItemProps {
  id: string;
  children?: React.ReactNode;
}

export const TransformableItem: React.FC<TransformableItemProps> = ({
  id,
  children,
}) => {
  const {
    elements,
    selected,
    onDragEnd,
    onMultiDragEnd,
    onMultiDragMove,
    onMultiResizeMove,
    onMultiRotateMove,
    onResizeEnd,
    onRotateEnd,
    onSelect,
    snapValue,
    theme,
    showOrigin,
  } = useTransformable();

  // Get element data from context using the id
  const element = elements.find((el: any) => el.id === id);
  if (!element) {
    console.warn(`TransformableItem: Element with id "${id}" not found`);
    return null;
  }

  const isSelected = selected.has(element.id);

  // Helper function to merge theme styles with defaults
  const getThemeStyle = (baseStyle: React.CSSProperties, themeStyle?: React.CSSProperties) => {
    return themeStyle ? { ...baseStyle, ...themeStyle } : baseStyle;
  };

  // Helper function to get resize handler style
  const getResizeHandlerStyle = (direction: string, baseStyle: React.CSSProperties) => {
    const baseHandlerStyle = theme.resizeHandlers!.base || {};
    const specificHandlerStyle = theme.resizeHandlers![direction] || {};
    return { ...baseStyle, ...baseHandlerStyle, ...specificHandlerStyle };
  };

  // Helper function to get rotation handler style
  const getRotationHandlerStyle = (direction: string, baseStyle: React.CSSProperties) => {
    const baseHandlerStyle = theme.rotationHandler!.base || {};
    const specificHandlerStyle = theme.rotationHandler![direction] || {};
    return { ...baseStyle, ...baseHandlerStyle, ...specificHandlerStyle };
  };

  const elementRef = useRef<HTMLDivElement>(null);
  const selectionOutlineRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ isDragging: boolean; startX: number; startY: number; offsetX: number; offsetY: number; isMultiDrag: boolean; isDraggingThis: boolean }>({ isDragging: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0, isMultiDrag: false, isDraggingThis: false });
  const resizeStateRef = useRef<{ isResizing: boolean; handle: string; startWidth: number; startHeight: number; startX: number; startY: number }>({ isResizing: false, handle: '', startWidth: 0, startHeight: 0, startX: 0, startY: 0 });
  const rotateStateRef = useRef<{ isRotating: boolean; startAngle: number; centerX: number; centerY: number }>({ isRotating: false, startAngle: 0, centerX: 0, centerY: 0 });
  const lastMousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const getCanvasCoordinates = useCallback((event: React.MouseEvent | MouseEvent) => {
    const canvasElement = document.querySelector('[data-canvas]') as HTMLElement;
    if (!canvasElement) return { x: 0, y: 0 };
    const rect = canvasElement.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }, []);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;
    
    const coords = getCanvasCoordinates(event);
    const target = event.target as HTMLElement;
    
    // Check if clicking on a resize handle
    if (target.classList.contains('resize-handle')) {
      const elementId = target.getAttribute('data-element-id');
      const handle = target.getAttribute('data-handle');
      if (elementId && handle) {
        resizeStateRef.current = {
          isResizing: true,
          handle,
          startWidth: element.width,
          startHeight: element.height,
          startX: coords.x,
          startY: coords.y,
        };
        onSelect(element.id, false);
      }
      return;
    }
    
    // Check if clicking on rotate handle
    if (target.classList.contains('rotate-handle')) {
      const elementId = target.getAttribute('data-element-id');
      const handle = target.getAttribute('data-handle');
      if (elementId && handle) {
        const centerX = element.x + element.origin[0] * element.width;
        const centerY = element.y + element.origin[1] * element.height;
        const rawAngle = Math.atan2(coords.y - centerY, coords.x - centerX) * 180 / Math.PI;
        const startAngle = rawAngle - element.rotation;
        rotateStateRef.current = {
          isRotating: true,
          startAngle,
          centerX,
          centerY,
        };
        onSelect(element.id, false);
      }
      return;
    }
    
    // Check if clicking on the element itself
    if (target.classList.contains('element')) {
      const elementId = target.getAttribute('data-element-id');
      if (elementId) {
        onSelect(elementId, event.shiftKey);
        
        // Check if this is a multi-drag (dragging a selected element)
        const isMultiDrag = selected.size > 1 && selected.has(element.id);
        const isDraggingThis = true; // This element is the one being dragged
        
        dragStateRef.current = {
          isDragging: true,
          startX: coords.x,
          startY: coords.y,
          offsetX: coords.x - element.x,
          offsetY: coords.y - element.y,
          isMultiDrag,
          isDraggingThis,
        };
      }
      return;
    }
  }, [element, selected, getCanvasCoordinates, onSelect]);

  const handleResizeMouseDown = useCallback((event: React.MouseEvent, handle: string) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    
    const coords = getCanvasCoordinates(event);
    
    resizeStateRef.current = {
      isResizing: true,
      handle,
      startWidth: element.width,
      startHeight: element.height,
      startX: coords.x,
      startY: coords.y,
    };
    onSelect(element.id, false);
  }, [element, selected, getCanvasCoordinates, onSelect]);

  const handleRotateMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    
    const coords = getCanvasCoordinates(event);
    const centerX = element.x + element.origin[0] * element.width;
    const centerY = element.y + element.origin[1] * element.height;
    const rawAngle = Math.atan2(coords.y - centerY, coords.x - centerX) * 180 / Math.PI;
    const startAngle = rawAngle - element.rotation;

    rotateStateRef.current = {
      isRotating: true,
      startAngle,
      centerX,
      centerY,
    };
    onSelect(element.id, false);
  }, [element, selected, getCanvasCoordinates, onSelect]);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const coords = getCanvasCoordinates(event);
    lastMousePosRef.current = coords;
    
    if (dragStateRef.current.isDragging) {
      const newX = snapValue(coords.x - dragStateRef.current.offsetX);
      const newY = snapValue(coords.y - dragStateRef.current.offsetY);
      
      if (dragStateRef.current.isMultiDrag && dragStateRef.current.isDraggingThis) {
        // Calculate delta for multi-drag and notify parent
        const deltaX = newX - element.x;
        const deltaY = newY - element.y;
        onMultiDragMove(deltaX, deltaY);
      }
      
      // Apply transformation directly for real-time feedback
      if (elementRef.current) {
        elementRef.current.style.left = `${newX}px`;
        elementRef.current.style.top = `${newY}px`;
      }
      
      // Update selection outline and handles
      if (selectionOutlineRef.current) {
        selectionOutlineRef.current.style.left = `${newX}px`;
        selectionOutlineRef.current.style.top = `${newY}px`;
      }
      
      // Update origin indicator position
      if (showOrigin) {
        const originElement = document.querySelector(`[data-origin-id="${element.id}"]`) as HTMLElement;
        if (originElement) {
          const originX = newX + element.origin[0] * element.width - 3;
          const originY = newY + element.origin[1] * element.height - 3;
          originElement.style.left = `${originX}px`;
          originElement.style.top = `${originY}px`;
        }
      }
    }
    
    if (resizeStateRef.current.isResizing) {
      const handleDeltaX = coords.x - resizeStateRef.current.startX;
      const handleDeltaY = coords.y - resizeStateRef.current.startY;
      
      let newWidth = element.width;
      let newHeight = element.height;
      let newX = element.x;
      let newY = element.y;
      
      if (resizeStateRef.current.handle.includes('right') || resizeStateRef.current.handle === 'right') {
        newWidth = Math.max(50, snapValue(resizeStateRef.current.startWidth + handleDeltaX));
      }
      if (resizeStateRef.current.handle.includes('left') || resizeStateRef.current.handle === 'left') {
        newWidth = Math.max(50, snapValue(resizeStateRef.current.startWidth - handleDeltaX));
        newX = snapValue(element.x + handleDeltaX);
      }
      if (resizeStateRef.current.handle.includes('bottom') || resizeStateRef.current.handle === 'bottom') {
        newHeight = Math.max(50, snapValue(resizeStateRef.current.startHeight + handleDeltaY));
      }
      if (resizeStateRef.current.handle.includes('top') || resizeStateRef.current.handle === 'top') {
        newHeight = Math.max(50, snapValue(resizeStateRef.current.startHeight - handleDeltaY));
        newY = snapValue(element.y + handleDeltaY);
      }
      
      // Check if this is a multi-resize
      const isMultiResize = selected.size > 1 && selected.has(element.id);
      
      if (isMultiResize) {
        // Calculate deltas for multi-resize and notify parent
        const deltaWidth = newWidth - element.width;
        const deltaHeight = newHeight - element.height;
        const deltaX = newX - element.x;
        const deltaY = newY - element.y;
        
        // Call the multi-resize callback
        onMultiResizeMove(deltaX, deltaY, deltaWidth, deltaHeight);
      }
      
      // Apply transformation directly for real-time feedback
      if (elementRef.current) {
        elementRef.current.style.left = `${newX}px`;
        elementRef.current.style.top = `${newY}px`;
        elementRef.current.style.width = `${newWidth}px`;
        elementRef.current.style.height = `${newHeight}px`;
      }
      
      // Update selection outline and handles
      if (selectionOutlineRef.current) {
        selectionOutlineRef.current.style.left = `${newX}px`;
        selectionOutlineRef.current.style.top = `${newY}px`;
        selectionOutlineRef.current.style.width = `${newWidth}px`;
        selectionOutlineRef.current.style.height = `${newHeight}px`;
      }
      
      // Update origin indicator position
      if (showOrigin) {
        const originElement = document.querySelector(`[data-origin-id="${element.id}"]`) as HTMLElement;
        if (originElement) {
          const originX = newX + element.origin[0] * newWidth - 3;
          const originY = newY + element.origin[1] * newHeight - 3;
          originElement.style.left = `${originX}px`;
          originElement.style.top = `${originY}px`;
        }
      }
    }
    
    if (rotateStateRef.current.isRotating) {
      const deltaX = coords.x - rotateStateRef.current.centerX;
      const deltaY = coords.y - rotateStateRef.current.centerY;
      const currentAngle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
      const newRotation = currentAngle - rotateStateRef.current.startAngle;
      
      // Check if this is a multi-rotate
      const isMultiRotate = selected.size > 1 && selected.has(element.id);
      
      if (isMultiRotate) {
        // Calculate delta rotation for multi-rotate
        const deltaRotation = newRotation - element.rotation;
        
        // Call the multi-rotate callback
        onMultiRotateMove(deltaRotation);
      }
      
      // Apply transformation directly for real-time feedback
      if (elementRef.current) {
        elementRef.current.style.transform = `rotate(${newRotation}deg)`;
      }
      
      // Update selection outline and handles rotation
      if (selectionOutlineRef.current) {
        selectionOutlineRef.current.style.transform = `rotate(${newRotation}deg)`;
      }
      
      // Update origin indicator position (don't rotate it)
      if (showOrigin) {
        const originElement = document.querySelector(`[data-origin-id="${element.id}"]`) as HTMLElement;
        if (originElement) {
          // Keep the origin indicator at the same position, don't rotate it
          const originX = element.x + element.origin[0] * element.width - 3;
          const originY = element.y + element.origin[1] * element.height - 3;
          originElement.style.left = `${originX}px`;
          originElement.style.top = `${originY}px`;
          originElement.style.transform = 'none';
        }
      }
    }
  }, [element, getCanvasCoordinates, snapValue, onMultiDragMove]);

  // Update origin indicator when element properties change (e.g., during multi-transformations)
  useEffect(() => {
    if (showOrigin) {
      const originElement = document.querySelector(`[data-origin-id="${element.id}"]`) as HTMLElement;
      if (originElement) {
        const originX = element.x + element.origin[0] * element.width - 3;
        const originY = element.y + element.origin[1] * element.height - 3;
        originElement.style.left = `${originX}px`;
        originElement.style.top = `${originY}px`;
        // Don't rotate the origin indicator - it should stay fixed at the origin point
        originElement.style.transform = 'none';
        originElement.style.transformOrigin = 'center';
      }
    }
  }, [element.x, element.y, element.width, element.height, element.rotation, element.origin, showOrigin]);

  const handleMouseUp = useCallback(() => {
    if (dragStateRef.current.isDragging) {
      const coords = lastMousePosRef.current;
      const newX = snapValue(coords.x - dragStateRef.current.offsetX);
      const newY = snapValue(coords.y - dragStateRef.current.offsetY);
      
      if (dragStateRef.current.isMultiDrag) {
        // Calculate delta for multi-drag
        const deltaX = newX - element.x;
        const deltaY = newY - element.y;
        
        // Create transformations for all selected elements
        const transformations: any[] = [];
        selected.forEach(id => {
          transformations.push({
            id,
            x: undefined, // Will be calculated by parent
            y: undefined, // Will be calculated by parent
            deltaX,
            deltaY,
          });
        });
        
        onMultiDragEnd(transformations);
      } else {
        // Single element drag
        onDragEnd({
          id: element.id,
          x: newX,
          y: newY,
        });
      }
      
      // Reset drag state
      dragStateRef.current = { isDragging: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0, isMultiDrag: false, isDraggingThis: false };
    }
    
    if (resizeStateRef.current.isResizing) {
      const coords = lastMousePosRef.current;
      const handleDeltaX = coords.x - resizeStateRef.current.startX;
      const handleDeltaY = coords.y - resizeStateRef.current.startY;
      
      let newWidth = element.width;
      let newHeight = element.height;
      let newX = element.x;
      let newY = element.y;
      
      if (resizeStateRef.current.handle.includes('right') || resizeStateRef.current.handle === 'right') {
        newWidth = Math.max(50, snapValue(resizeStateRef.current.startWidth + handleDeltaX));
      }
      if (resizeStateRef.current.handle.includes('left') || resizeStateRef.current.handle === 'left') {
        newWidth = Math.max(50, snapValue(resizeStateRef.current.startWidth - handleDeltaX));
        newX = snapValue(element.x + handleDeltaX);
      }
      if (resizeStateRef.current.handle.includes('bottom') || resizeStateRef.current.handle === 'bottom') {
        newHeight = Math.max(50, snapValue(resizeStateRef.current.startHeight + handleDeltaY));
      }
      if (resizeStateRef.current.handle.includes('top') || resizeStateRef.current.handle === 'top') {
        newHeight = Math.max(50, snapValue(resizeStateRef.current.startHeight - handleDeltaY));
        newY = snapValue(element.y + handleDeltaY);
      }
      
      onResizeEnd({
        id: element.id,
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      });
      
      // Reset resize state
      resizeStateRef.current = { isResizing: false, handle: '', startWidth: 0, startHeight: 0, startX: 0, startY: 0 };
    }
    
    if (rotateStateRef.current.isRotating) {
      const coords = lastMousePosRef.current;
      const deltaX = coords.x - rotateStateRef.current.centerX;
      const deltaY = coords.y - rotateStateRef.current.centerY;
      const currentAngle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
      const newRotation = currentAngle - rotateStateRef.current.startAngle;
      
      onRotateEnd({
        id: element.id,
        rotation: newRotation,
      });
      
      // Reset rotate state
      rotateStateRef.current = { isRotating: false, startAngle: 0, centerX: 0, centerY: 0 };
    }
  }, [element, selected, snapValue, onDragEnd, onMultiDragEnd, onResizeEnd, onRotateEnd]);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <>
      <div
        ref={elementRef}
        className="element"
        data-element-id={element.id}
        style={getThemeStyle({
          position: 'absolute',
          left: element.x,
          top: element.y,
          width: element.width,
          height: element.height,
          borderRadius: '0px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          cursor: 'move',
          transform: `rotate(${element.rotation}deg)`,
          transformOrigin: `${element.origin[0] * 100}% ${element.origin[1] * 100}%`,
          userSelect: 'none',
          zIndex: element.zIndex,
        }, isSelected ? theme.element : undefined)}
        onMouseDown={handleMouseDown}
      >
        {children}
      </div>
      
      {/* Origin indicator */}
      {isSelected && showOrigin && (
        <div
          data-origin-id={element.id}
          style={{
            ...theme.origin,
            left: element.x + element.origin[0] * element.width - 3,
            top: element.y + element.origin[1] * element.height - 3,
            transform: 'none',
            transformOrigin: 'center',
          }}
        />
      )}
      
      {/* Selection outline and handles */}
      {isSelected && (
        <div
          ref={selectionOutlineRef}
          style={getThemeStyle({
            position: 'absolute',
            left: element.x,
            top: element.y,
            width: element.width,
            height: element.height,
            outline: '1px solid #007bff',
            borderRadius: '0px',
            transform: `rotate(${element.rotation}deg)`,
            transformOrigin: `${element.origin[0] * 100}% ${element.origin[1] * 100}%`,
            pointerEvents: 'none',
            zIndex: 9999,
          }, theme.element)}
        >
          {/* Resize handles - positioned relative to selection outline */}
          <div 
            className="resize-handle" 
            data-element-id={element.id} 
            data-handle="top-left" 
            style={getResizeHandlerStyle('topLeft', {
              position: 'absolute', 
              left: -4.5, 
              top: -4.5, 
              width: 8, 
              height: 8, 
              backgroundColor: 'white', 
              outline: '1px solid #007bff', 
              borderRadius: 0, 
              cursor: 'nw-resize', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={(e) => handleResizeMouseDown(e, 'top-left')}
          />
          <div 
            className="resize-handle" 
            data-element-id={element.id} 
            data-handle="top" 
            style={getResizeHandlerStyle('top', {
              position: 'absolute', 
              left: '50%', 
              top: -4.5, 
              width: 8, 
              height: 8, 
              backgroundColor: 'white', 
              outline: '1px solid #007bff', 
              borderRadius: 0, 
              cursor: 'n-resize', 
              transform: 'translateX(-50%)', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
          />
          <div 
            className="resize-handle" 
            data-element-id={element.id} 
            data-handle="top-right" 
            style={getResizeHandlerStyle('topRight', {
              position: 'absolute', 
              right: -4.5, 
              top: -4.5, 
              width: 8, 
              height: 8, 
              backgroundColor: 'white', 
              outline: '1px solid #007bff', 
              borderRadius: 0, 
              cursor: 'ne-resize', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={(e) => handleResizeMouseDown(e, 'top-right')}
          />
          <div 
            className="resize-handle" 
            data-element-id={element.id} 
            data-handle="right" 
            style={getResizeHandlerStyle('right', {
              position: 'absolute', 
              right: -4.5, 
              top: '50%', 
              width: 8, 
              height: 8, 
              backgroundColor: 'white', 
              outline: '1px solid #007bff', 
              borderRadius: 0, 
              cursor: 'e-resize', 
              transform: 'translateY(-50%)', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
          />
          <div 
            className="resize-handle" 
            data-element-id={element.id} 
            data-handle="bottom-right" 
            style={getResizeHandlerStyle('bottomRight', {
              position: 'absolute', 
              right: -4.5, 
              bottom: -4, 
              width: 8, 
              height: 8, 
              backgroundColor: 'white', 
              outline: '1px solid #007bff', 
              borderRadius: 0, 
              cursor: 'se-resize', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-right')}
          />
          <div 
            className="resize-handle" 
            data-element-id={element.id} 
            data-handle="bottom" 
            style={getResizeHandlerStyle('bottom', {
              position: 'absolute', 
              left: '50%', 
              bottom: -4.5, 
              width: 8, 
              height: 8, 
              backgroundColor: 'white', 
              outline: '1px solid #007bff', 
              borderRadius: 0, 
              cursor: 's-resize', 
              transform: 'translateX(-50%)', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
          />
          <div 
            className="resize-handle" 
            data-element-id={element.id} 
            data-handle="bottom-left" 
            style={getResizeHandlerStyle('bottomLeft', {
              position: 'absolute', 
              left: -4.5, 
              bottom: -4, 
              width: 8, 
              height: 8, 
              backgroundColor: 'white', 
              outline: '1px solid #007bff', 
              borderRadius: 0, 
              cursor: 'sw-resize', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-left')}
          />
          <div 
            className="resize-handle" 
            data-element-id={element.id} 
            data-handle="left" 
            style={getResizeHandlerStyle('left', {
              position: 'absolute', 
              left: -4.5, 
              top: '50%', 
              width: 8, 
              height: 8, 
              backgroundColor: 'white', 
              outline: '1px solid #007bff', 
              borderRadius: 0, 
              cursor: 'w-resize', 
              transform: 'translateY(-50%)', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
          />
          
          {/* Rotation handles - positioned relative to selection outline */}
          <div 
            className="rotate-handle" 
            data-element-id={element.id} 
            data-handle="rotate-top-left" 
            style={getRotationHandlerStyle('topLeft', {
              position: 'absolute', 
              left: -12, 
              top: -12, 
              width: 8, 
              height: 8, 
              backgroundColor: 'transparent', 
              border: 'none', 
              borderRadius: 0, 
              cursor: 'grab', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={handleRotateMouseDown}
          />
          <div 
            className="rotate-handle" 
            data-element-id={element.id} 
            data-handle="rotate-top-right" 
            style={getRotationHandlerStyle('topRight', {
              position: 'absolute', 
              right: -12, 
              top: -12, 
              width: 8, 
              height: 8, 
              backgroundColor: 'transparent', 
              border: 'none', 
              borderRadius: 0, 
              cursor: 'grab', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={handleRotateMouseDown}
          />
          <div 
            className="rotate-handle" 
            data-element-id={element.id} 
            data-handle="rotate-bottom-right" 
            style={getRotationHandlerStyle('bottomRight', {
              position: 'absolute', 
              right: -12, 
              bottom: -12, 
              width: 8, 
              height: 8, 
              backgroundColor: 'transparent', 
              border: 'none', 
              borderRadius: 0, 
              cursor: 'grab', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={handleRotateMouseDown}
          />
          <div 
            className="rotate-handle" 
            data-element-id={element.id} 
            data-handle="rotate-bottom-left" 
            style={getRotationHandlerStyle('bottomLeft', {
              position: 'absolute', 
              left: -12, 
              bottom: -12, 
              width: 8, 
              height: 8, 
              backgroundColor: 'transparent', 
              border: 'none', 
              borderRadius: 0, 
              cursor: 'grab', 
              pointerEvents: 'auto',
              zIndex: 10000,
            })} 
            onMouseDown={handleRotateMouseDown}
          />
        </div>
      )}
    </>
  );
};
